import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useGroupsByOfficeQuery } from "@/hooks/useGroupsByOfficeQuery";

const historySchema = z.object({ items: z.array(z.object({
  id: z.number(), reason: z.string(), status: z.string(), start_date: z.string(),
  end_date: z.string().nullable().optional(),
  expected_end_date: z.string().nullable().optional(),
  target_group_id: z.string().nullable().optional(),
  target_group_name: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})) });
const statusLabels: Readonly<Record<string, string>> = {
  completed: "완료", cancelled: "취소", on_hold: "보류",
};

export function DispatchHistory({ nurseId, groupId, officeId, accountId }: {
  readonly nurseId: string;
  readonly groupId: string;
  readonly officeId: string;
  readonly accountId: string;
}) {
  const groups = useGroupsByOfficeQuery({ enabled: Boolean(officeId), officeId });
  const history = useQuery({
    queryKey: QUERY_KEYS.nurses.dispatchHistory(accountId, officeId, nurseId, groupId),
    queryFn: async ({ signal }) => historySchema.parse(await httpClient.get<unknown>(
      "/nurses/assignments",
      { params: { nurse_id: nurseId, group_id: groupId, status: "all" }, signal },
    )),
    enabled: Boolean(nurseId && groupId && officeId && accountId),
  });
  const items = history.data?.items.filter(item => item.reason === "파견" && item.status !== "active") ?? [];

  return <section aria-label="이전 파견 기록" className="mt-4 flex flex-col gap-2">
    <h4 className="border-b pb-1 text-sm font-semibold text-muted-foreground">이전 파견 기록</h4>
    {history.isPending ? <p role="status" className="text-sm text-muted-foreground">파견 기록을 불러오는 중입니다.</p>
      : history.isError ? <div role="alert" className="text-sm text-muted-foreground">
        파견 기록을 불러오지 못했습니다.
        <button type="button" className="ml-2 underline" onClick={() => { void history.refetch(); }}>다시 시도</button>
      </div>
      : items.length === 0 ? <p className="text-sm text-muted-foreground">이전 파견 기록이 없습니다.</p>
      : items.map(item => <div key={item.id} className="rounded-md border px-3 py-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-base text-foreground">{item.target_group_name || groups.data?.find(group => group.group_id === item.target_group_id)?.group_name || item.target_group_id || "-"}</span>
          <span className="text-sm text-muted-foreground">{item.start_date} ~ {item.end_date ?? item.expected_end_date ?? "종료일 미정"}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{statusLabels[item.status] ?? item.status}</span>
        </div>
        <p className="mt-1 break-words text-xs text-muted-foreground">{item.note || "-"}</p>
      </div>)}
  </section>;
}
